import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { Crown, TrendingUp, DollarSign } from "lucide-react";

const CustomersCard = ({ customers = [], orders = [] }) => {
  const [activeTab, setActiveTab] = useState("all");

  // 🔥 derive customer groups
  const { bookedCustomers, customersWithNoOrders } = useMemo(() => {
    const orderUserIds = new Set(orders.map((o) => o.user?.id || o.user?._id));

    const booked = customers.filter((c) => orderUserIds.has(c.id || c._id));

    const noOrders = customers.filter((c) => !orderUserIds.has(c.id || c._id));

    return {
      bookedCustomers: booked,
      customersWithNoOrders: noOrders,
    };
  }, [customers, orders]);

  // 🔥 INSIGHTS
  const { topSpender, avgSpend, newCustomersThisWeek } = useMemo(() => {
    if (!orders.length) {
      return {
        topSpender: null,
        avgSpend: 0,
        newCustomersThisWeek: 0,
      };
    }

    const spendMap = {};
    const firstOrderDate = {};

    orders.forEach((order) => {
      const userId = order.user?.id || order.user?._id;
      const name =
        order.user?.displayName || order.customer_info?.firstname || "Customer";

      if (!userId) return;

      // 💰 total spend
      spendMap[userId] = {
        name,
        total: (spendMap[userId]?.total || 0) + (order.totalPrice || 0),
      };

      // 📊 first order date
      const orderDate = moment(order.createdAt);
      if (
        !firstOrderDate[userId] ||
        orderDate.isBefore(firstOrderDate[userId])
      ) {
        firstOrderDate[userId] = orderDate;
      }
    });

    // 🥇 top spender
    const top = Object.values(spendMap).sort((a, b) => b.total - a.total)[0];

    // 💰 avg spend
    const totalRevenue = Object.values(spendMap).reduce(
      (sum, u) => sum + u.total,
      0,
    );

    const avg =
      Object.keys(spendMap).length > 0
        ? (totalRevenue / Object.keys(spendMap).length).toFixed(0)
        : 0;

    // 📊 new customers this week
    const now = moment();
    const newThisWeek = Object.values(firstOrderDate).filter((date) =>
      date.isAfter(now.clone().subtract(7, "days")),
    ).length;

    return {
      topSpender: top,
      avgSpend: avg,
      newCustomersThisWeek: newThisWeek,
    };
  }, [orders]);

  const currentList =
    activeTab === "all"
      ? customers
      : activeTab === "booked"
        ? bookedCustomers
        : customersWithNoOrders;

  return (
    <div className="rounded-2xl border border-navy shadow-sm p-6">
      {/* 🔥 HEADER */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-400">
            Customers
          </p>

          <div className="flex items-end gap-3 mt-2">
            <h2 className="text-4xl font-bold text-gray-900">
              {customers.length}
            </h2>
            <span className="text-sm text-gray-400 pb-1">total customers</span>
          </div>
        </div>

        <div className="px-3 py-1 text-xs rounded-full bg-green-50 text-green-600 font-medium">
          Live
        </div>
        {/* 🔥 INSIGHTS WITH ICONS */}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 w-full">
        <div className="group p-4 rounded-xl border border-gray-300 hover:bg-gray-50 transition">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400">
              <Crown className="w-4 h-4 text-yellow-500" />
              Top Customer
            </div>
          </div>

          <div className="text-sm font-semibold text-gray-900 truncate">
            {topSpender?.name || "N/A"}
          </div>

          <div className="text-xs text-gray-400 mt-1">
            ${Number(topSpender?.total || 0).toFixed(0)} spent
          </div>
        </div>

        <div className="group p-4 rounded-xl border border-gray-300  hover:bg-gray-50 transition">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400 mb-2">
            <TrendingUp className="w-4 h-4 text-green-600" />
            Growth
          </div>

          <div
            className={`text-lg font-semibold ${
              newCustomersThisWeek > 0 ? "text-green-600" : "text-gray-400"
            }`}
          >
            +{newCustomersThisWeek}
          </div>

          <div className="text-xs text-gray-400 mt-1">
            new customers this week
          </div>
        </div>

        <div className="group p-4 rounded-xl border border-gray-300  hover:bg-gray-50 transition col-span-1">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400 mb-2">
            <DollarSign className="w-4 h-4 text-blue-600" />
            Average Spend
          </div>

          <div className="text-lg font-semibold text-gray-900">
            ${Number(avgSpend).toFixed(0)}
          </div>

          <div className="text-xs text-gray-400 mt-1">per customer</div>
        </div>
      </div>
      {/* 🔥 TABS */}
      <div className="flex my-4 gap-3 text-sm font-medium mt-4">
        {["all", "booked", "no-orders"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1 rounded-full ${
              activeTab === tab
                ? "bg-navy text-yellowstar"
                : "text-gray-400 hover:text-gray-900"
            }`}
          >
            {tab === "all" && `All (${customers.length})`}
            {tab === "booked" && `Ordered (${bookedCustomers.length})`}
            {tab === "no-orders" &&
              `No Orders (${customersWithNoOrders.length})`}
          </button>
        ))}
      </div>

      {/* 🔥 LIST */}
      <div className="max-h-[350px] overflow-y-auto space-y-2 pr-2">
        {currentList.length === 0 ? (
          <p className="text-gray-400 text-sm">No customers found.</p>
        ) : (
          currentList.map((member, index) => (
            <Link
              to={`/customer/preview/${member.id || member._id}`}
              key={member.id || member._id}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl hover:bg-gray-50 transition"
            >
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-5">{index + 1}.</span>

                <img
                  src={member.photo || "/default-avatar.png"}
                  alt={member.displayName}
                  className="w-10 h-10 rounded-full object-cover"
                />

                <div className="text-xs">
                  <h3 className="font-medium text-gray-900 truncate max-w-[140px]">
                    {member.displayName}
                  </h3>
                  <p className="text-gray-400 truncate">{member.phone}</p>
                </div>
              </div>

              <span className="text-xs text-gray-400">
                {new Date(member.updatedAt).toLocaleDateString("en-NZ", {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </Link>
          ))
        )}
      </div>
    </div>
  );
};

export default CustomersCard;
